import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps, CSSProperties } from "react";
import { cn } from "../../lib/utils";

export const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        showcase: "border border-transparent bg-primary text-primary-foreground hover:bg-primary/90 backdrop-blur-sm",
        "showcase-outline": "border border-input bg-background/75 text-foreground hover:bg-background backdrop-blur-sm",
        "showcase-text": `button-motion-text border-0 bg-transparent text-inherit
  [&&.button-motion-text]:[background:transparent] [&&.button-motion-text]:border-0 [&&.button-motion-text]:[box-shadow:none] [&&.button-motion-text]:min-w-0 [&&.button-motion-text]:px-4
  [&.button-motion-text:is(:hover,:focus-visible,:active)]:[background:transparent] [&.button-motion-text:is(:hover,:focus-visible,:active)]:border-0 [&.button-motion-text:is(:hover,:focus-visible,:active)]:[box-shadow:none]
  [&.button-motion-text::before]:hidden
  [&.button-motion-text:is(:hover,:focus-visible)]:[transform:scale(1.25)]
`,
        default: "border border-transparent bg-primary text-primary-foreground hover:bg-primary/90",
        info: "border border-transparent bg-info text-white hover:bg-info/90",
        success: "border border-transparent bg-success text-white hover:bg-success/90",
        outline: "border border-input bg-background text-foreground hover:bg-accent",
        secondary: "border border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        subtle: "border border-border bg-muted/30 text-muted-foreground shadow-xs hover:border-input hover:bg-muted hover:text-foreground active:bg-muted/80 aria-expanded:border-input aria-expanded:bg-muted aria-expanded:text-foreground",
        ghost: "border border-transparent text-foreground hover:bg-accent",
        destructive: "border border-transparent bg-destructive text-white hover:bg-destructive/90",
        link: "text-foreground underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-3 py-2",
        sm: "h-8 gap-1.5 px-2.5 text-xs",
        lg: "h-10 px-5",
        icon: "size-9 p-0",
        "icon-sm": "size-8 p-0",
      },
      motion: { true: `button-motion
  [&.button-motion]:relative [&.button-motion]:isolate [&.button-motion]:overflow-hidden [&.button-motion]:[min-width:132px] [&.button-motion]:h-12 [&.button-motion]:[padding:0_24px] [&.button-motion]:[border-radius:10px] [&.button-motion]:[transition:transform_.24s_cubic-bezier(.2,.8,.2,1),box-shadow_.24s,background_.24s] [&.button-motion]:[box-shadow:inset_0_1px_0_#ffffff29,0_5px_12px_#17372a14]
  [&::before]:[content:""] [&::before]:absolute [&::before]:[inset:-50%] [&::before]:[background:linear-gradient(110deg,transparent_36%,#ffffff33_48%,#ffffff55_50%,transparent_64%)] [&::before]:[transform:translateX(-130%)] [&::before]:pointer-events-none
  [&:is(:hover,:focus-visible)]:[transform:translateY(-2px)] [&:is(:hover,:focus-visible)]:[box-shadow:inset_0_1px_0_#ffffff66,0_8px_20px_#17372a21]
  [&:is(:hover,:focus-visible)::before]:animate-[button-sheen_.8s_ease_both]
  [&:not(.button-motion-text):is(:hover,:focus-visible)_.button-motion-label>span]:animate-[button-letter_.5s_cubic-bezier(.2,.7,.3,1)_both] [&:not(.button-motion-text):is(:hover,:focus-visible)_.button-motion-label>span]:[animation-delay:var(--letter-delay)]
  [&:active]:[transform:translateY(0)_scale(.98)]
  motion-reduce:animate-none! motion-reduce:[transition:none]! motion-reduce:[transform:none]!
  motion-reduce:[&::before]:animate-none! motion-reduce:[&::before]:[transition:none]! motion-reduce:[&::before]:[transform:none]! motion-reduce:[&::before]:hidden
`, false: "" },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export function Button({ asChild, className, variant, size, motion, type = "button", ...props }: ComponentProps<"button"> & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Component = asChild ? Slot : "button";
  return <Component data-slot="button" className={cn(buttonVariants({ variant, size, motion }), className)} {...(!asChild ? { type } : {})} {...props} />;
}

export function ButtonMotionLabel({ text }: { text: string }) {
  return <span className={`button-motion-label
  relative inline-flex whitespace-pre
  [&>span]:inline-block
  motion-reduce:[&>span]:animate-none! motion-reduce:[&>span]:[transition:none]! motion-reduce:[&>span]:[transform:none]!
`} aria-hidden="true">{Array.from(text).map((char, index) =>
    <span key={index} style={{ "--letter-delay": `${index * 35}ms` } as CSSProperties}>{char}</span>)}</span>;
}
